import { useQuery } from '@tanstack/react-query'
import { useSearch } from '@tanstack/react-router'
import type { OnChangeFn, PaginationState, SortingState } from '@tanstack/react-table'
import { ArrowLeft, Plus } from 'lucide-react'
import { useState } from 'react'

import { savingsProductListQueryOptions } from '@/api/savings-api'
import type { SavingsProduct } from '@/api/savings-types'
import { AuditTrailDrawer } from '@/components/audit-trail-drawer'
import { DataTable, dataTableColumns, type DataTableColumn } from '@/components/data-table'
import { MoneyDisplay } from '@/components/money-display'
import { PageHeader } from '@/components/page-header'
import { RequiresWiderScreen } from '@/components/requires-wider-screen'
import { Badge } from '@/components/ui/badge'
import { Button } from '@/components/ui/button'
import { Checkbox } from '@/components/ui/checkbox'
import { Label } from '@/components/ui/label'
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select'
import { InterestRate } from '@/features/savings/account-panels'
import {
  SAVINGS_PRODUCT_CLASSES,
  interestFrequencyLabel,
  productClassLabel,
} from '@/features/savings/constants'
import { savingsPaths, useSavingsNavigate } from '@/features/savings/navigation'
import { useSavingsPermissions } from '@/features/savings/permissions'
import { NewProductDialog } from '@/features/savings/product-dialog'

import { savingsProductListSearchSchema, type SavingsProductListSearch } from './search'

const UNSORTED: SortingState = []
const ignoreSortingChange: OnChangeFn<SortingState> = () => undefined
const ANY = 'any'

/**
 * The savings products.
 *
 * **Refuses below `lg`.** The mobile policy puts product configuration in the refusing column —
 * "rarely urgent, permanently consequential" — and it is right about this one specifically: the
 * rate, the fees, the dormancy period and the three ledger accounts set here are carried by every
 * account ever opened against the product, and the class can never be changed afterwards.
 *
 * The endpoint serves an array rather than a page, so the table pages the array it was given. That
 * is honest here in a way it would not be on the account list: a chart of savings products is
 * measured in tens, and the server has no paging parameter to defer to.
 */
const helper = dataTableColumns<SavingsProduct>()

const columns: DataTableColumn<SavingsProduct>[] = helper.columns([
  helper.accessor('code', {
    header: 'Code',
    enableSorting: false,
    cell: (context) => <span className="font-mono text-xs">{context.row.original.code}</span>,
    meta: { noWrap: true, widthClass: 'w-32', card: 'lead' },
  }),
  helper.accessor('name', {
    header: 'Name',
    enableSorting: false,
    cell: (context) => (
      <span className="font-medium text-heading">{context.row.original.name}</span>
    ),
    meta: { card: 'title' },
  }),
  helper.accessor('productClass', {
    header: 'Class',
    enableSorting: false,
    cell: (context) => (
      <div className="flex flex-wrap items-center gap-1.5">
        <span>{productClassLabel(context.row.original.productClass)}</span>
        {!context.row.original.isWithdrawable ? <Badge tone="warning">Locked</Badge> : null}
      </div>
    ),
    meta: { noWrap: true, widthClass: 'w-44' },
  }),
  helper.accessor('interestRatePerAnnum', {
    header: 'Interest',
    enableSorting: false,
    // The fraction the API serves is converted for display here and nowhere else on the read
    // path. 0.075 is 7.5%, and printing the fraction with a % sign would understate every rate in
    // this column by two orders of magnitude.
    cell: (context) => (
      <div className="space-y-0.5">
        <InterestRate fraction={context.row.original.interestRatePerAnnum} />
        <p className="text-2xs text-muted">
          {interestFrequencyLabel(context.row.original.interestPostingFrequency)}
        </p>
      </div>
    ),
    meta: { widthClass: 'w-44' },
  }),
  helper.accessor('minOpeningBalanceMinor', {
    header: 'Min. opening',
    enableSorting: false,
    cell: (context) => (
      <MoneyDisplay
        value={context.row.original.minOpeningBalanceMinor}
        currency={context.row.original.currencyCode}
        symbol={false}
      />
    ),
    meta: { align: 'right', widthClass: 'w-32' },
  }),
  helper.accessor('minBalanceMinor', {
    header: 'Min. balance',
    enableSorting: false,
    cell: (context) => (
      <MoneyDisplay
        value={context.row.original.minBalanceMinor}
        currency={context.row.original.currencyCode}
        symbol={false}
      />
    ),
    meta: { align: 'right', widthClass: 'w-32' },
  }),
  helper.accessor('withdrawalFeeMinor', {
    header: 'Withdrawal fee',
    enableSorting: false,
    // Null and zero are different products. A dash says "charges nothing"; 0.00 would say "charges
    // a fee, of nothing", and the second posts a fee line the first never does.
    cell: (context) =>
      context.row.original.withdrawalFeeMinor === null ? (
        <span className="block text-right text-muted">No fee</span>
      ) : (
        <MoneyDisplay
          value={context.row.original.withdrawalFeeMinor}
          currency={context.row.original.currencyCode}
          symbol={false}
        />
      ),
    meta: { align: 'right', widthClass: 'w-36' },
  }),
  helper.accessor('dormancyDays', {
    header: 'Dormancy',
    enableSorting: false,
    cell: (context) =>
      context.row.original.productClass === 'SecurityDeposit' ? (
        <span className="text-muted">Never</span>
      ) : context.row.original.dormancyDays === 0 ? (
        <span className="text-muted">Never</span>
      ) : (
        <span className="numeric tabular-nums">{context.row.original.dormancyDays} days</span>
      ),
    meta: { noWrap: true, widthClass: 'w-32' },
  }),
  helper.accessor('liabilityGlAccountCode', {
    header: 'Ledger accounts',
    enableSorting: false,
    cell: (context) => (
      <div className="space-y-0.5 font-mono text-2xs text-muted">
        <p>liability {context.row.original.liabilityGlAccountCode}</p>
        <p>interest {context.row.original.interestExpenseGlAccountCode}</p>
        <p>fees {context.row.original.feeIncomeGlAccountCode}</p>
      </div>
    ),
    meta: { noWrap: true },
  }),
  helper.accessor('isActive', {
    header: 'State',
    enableSorting: false,
    cell: (context) =>
      context.row.original.isActive ? (
        <Badge tone="success">Active</Badge>
      ) : (
        <Badge tone="neutral">Retired</Badge>
      ),
    meta: { widthClass: 'w-28', card: 'lead' },
  }),
])

export function SavingsProductsPage() {
  const navigate = useSavingsNavigate()
  const permissions = useSavingsPermissions()

  const search: SavingsProductListSearch = savingsProductListSearchSchema.parse(
    useSearch({ strict: false }),
  )

  const [defining, setDefining] = useState(false)
  const [auditing, setAuditing] = useState<SavingsProduct | null>(null)
  const [pagination, setPagination] = useState<PaginationState>({ pageIndex: 0, pageSize: 25 })

  const query = useQuery(
    savingsProductListQueryOptions({
      productClass: search.productClass,
      includeInactive: search.includeInactive,
    }),
  )

  const updateSearch = (patch: Partial<SavingsProductListSearch>) => {
    navigate(savingsPaths.products(), {
      search: { productClass: search.productClass, includeInactive: search.includeInactive, ...patch },
      replace: true,
    })
  }

  const all = query.data ?? []
  const start = pagination.pageIndex * pagination.pageSize
  const rows = all.slice(start, start + pagination.pageSize)

  return (
    <RequiresWiderScreen
      screen="Savings products"
      reason="A product sets the rate, the fees, the dormancy period, and the ledger accounts that every account opened against it will carry for its whole life — and its class can never be changed afterwards. It deserves the whole picture at once."
      backTo="/savings"
      backLabel="Back to savings"
    >
      <button
        type="button"
        onClick={() => navigate(savingsPaths.accounts())}
        className="inline-flex items-center gap-1.5 text-xs font-medium text-brand-text hover:text-brand-text-hover"
      >
        <ArrowLeft className="size-3.5" aria-hidden />
        Back to savings accounts
      </button>

      <PageHeader
        title="Savings products"
        description="The terms accounts are opened under. Interest rates are shown as percentages; they cross the wire as a fraction between zero and one."
        actions={
          permissions.canManageProducts ? (
            <Button variant="primary" onClick={() => setDefining(true)}>
              <Plus />
              Define a product
            </Button>
          ) : null
        }
      />

      <div className="space-y-3">
        <div className="flex flex-wrap items-end gap-4 rounded-card border border-border-subtle bg-surface p-3">
          <div className="min-w-44 space-y-1.5">
            <Label htmlFor="product-filter-class">Class</Label>
            <Select
              value={search.productClass ?? ANY}
              onValueChange={(next) => {
                updateSearch({
                  productClass:
                    next === ANY ? undefined : (next as SavingsProductListSearch['productClass']),
                })
              }}
            >
              <SelectTrigger id="product-filter-class">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value={ANY}>Both classes</SelectItem>
                {SAVINGS_PRODUCT_CLASSES.map((candidate) => (
                  <SelectItem key={candidate} value={candidate}>
                    {productClassLabel(candidate)}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          <div className="flex items-start gap-2 pb-2">
            <Checkbox
              id="product-filter-inactive"
              checked={search.includeInactive === true}
              onCheckedChange={(checked) => {
                updateSearch({ includeInactive: checked === true ? true : undefined })
              }}
              className="mt-0.5"
            />
            <div className="space-y-0.5">
              <Label htmlFor="product-filter-inactive" className="text-sm font-normal text-body">
                Include retired products
              </Label>
              <p className="text-xs text-muted">
                A retired product takes no new accounts and keeps every account already on it.
              </p>
            </div>
          </div>
        </div>

        <DataTable
          columns={columns}
          data={query.isPending ? undefined : rows}
          rowCount={all.length}
          pagination={pagination}
          onPaginationChange={setPagination}
          sorting={UNSORTED}
          onSortingChange={ignoreSortingChange}
          isLoading={query.isPending}
          isFetching={query.isFetching}
          error={query.isError ? query.error : null}
          onRetry={() => void query.refetch()}
          getRowId={(row) => row.id}
          onRowActivate={setAuditing}
          emptyTitle="No products"
          emptyDescription={
            search.productClass || search.includeInactive
              ? 'Nothing matches those filters.'
              : 'No savings product has been defined yet. Nothing can be opened until one is, and defining one is countersigned.'
          }
          emptyAction={
            permissions.canManageProducts ? (
              <Button variant="primary" size="sm" onClick={() => setDefining(true)}>
                <Plus />
                Define a product
              </Button>
            ) : null
          }
        />

        <p className="text-xs text-muted">
          Opening a row shows its audit trail — every change to a product, and every interest run
          made against it, is recorded there.
        </p>
      </div>

      <NewProductDialog open={defining} onOpenChange={setDefining} />

      {/*
        Interest runs are audited under the product, not under the accounts they touched, so the
        product's trail is where a run of forty accounts appears as one entry. That is the only
        place it appears at all.
      */}
      <AuditTrailDrawer
        open={auditing !== null}
        onOpenChange={(open) => {
          if (!open) {
            setAuditing(null)
          }
        }}
        entityType="SavingsProduct"
        entityId={auditing?.id ?? ''}
        subject={auditing ? `${auditing.code} — ${auditing.name}` : undefined}
      />
    </RequiresWiderScreen>
  )
}
